import Select, { components } from 'react-select';
import CustomToolTip from './CustomToolTip';
import React, { memo, useMemo, useState, useCallback } from 'react';
import { CircularProgress } from '@mui/material';
import { uuidv4 } from '../../utils/commonFunctions';

interface CustomSelectProps {
    options: any[];
    placeholder: string;
    value: any;
    onChange: (value: any) => void;
    getOptionValue?: (option: any) => string;
    isDisabled?: boolean;
    isSearchable?: boolean;
    isMulti?: boolean;
    closeMenuOnSelect?: boolean;
    hideSelectedOptions?: boolean;
    overrideComponents?: Record<string, any>;
    border?: string;
    color?: string;
    menuHeight?: string;
    placement?: 'top' | 'bottom' | 'auto';
    background?: string;
    padding?: string;
    controllHeight?: string;
    id?: string;
    fullWidth?: boolean;
    minWidth?: string;
    maxWidth?: string;
    isLoading?: boolean;
    mutedLoadingState?: boolean;
    filterOption?: ((option: any, rawInput: string) => boolean) | null;
    inputValue?: string;
    onInputChange?: (value: string, actionMeta: any) => string | void;
    forceCloseMenu?: boolean;
}

const CustomSelect: React.FC<CustomSelectProps> = ({
    options,
    placeholder,
    value,
    onChange,
    getOptionValue,
    isDisabled,
    border,
    color,
    menuHeight,
    placement = 'auto',
    background,
    padding,
    isSearchable,
    isMulti,
    closeMenuOnSelect,
    hideSelectedOptions,
    overrideComponents,
    controllHeight,
    id,
    fullWidth = true,
    minWidth,
    maxWidth,
    isLoading,
    mutedLoadingState = false,
    inputValue,
    onInputChange,
    filterOption,
    forceCloseMenu = false,
}) => {
    const uniqueId = useMemo(() => id || uuidv4(), [id]);
    const [openMenu, setOpenMenu] = useState(false);
    const usesControlledInput = inputValue !== undefined;

    // Memoized option
    const CustomOption = useCallback((props: any) => {
        const { data } = props;
        return (
            <components.Option {...props}>
                <CustomToolTip title={data.label} maxWidth='200px' placement='right'>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {data.icon}
                        <span>{data.label}</span>
                    </div>
                </CustomToolTip>
            </components.Option>
        );
    }, []);

    // Memoized single value
    const CustomSingleValue = useCallback((props: any) => {
        const { data } = props;
        return (
            <components.SingleValue {...props}>
                <div style={{ display: "flex", alignItems: "center", gap: "6px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {data.icon}
                    <span>{data.label}</span>
                </div>
            </components.SingleValue>
        );
    }, []);

    // Memoized control
    const CustomControl = useMemo(() => {
        return memo(({ children, ...props }: any) => {
            return (
                <components.Control {...props}>{children}</components.Control>
            );
        });
    }, []);

    const CustomLoadingIndicator = useCallback(() => (
        mutedLoadingState
            ? <CircularProgress size={16} sx={{ color: 'rgba(240, 240, 240, 0.45)' }} />
            : <CircularProgress size={16} color='inherit' />
    ), [mutedLoadingState]);

    const handleInputChange = useCallback((newValue: string, actionMeta: any) => {
        if (actionMeta?.action !== 'input-change') return newValue;
        return onInputChange?.(newValue, actionMeta) ?? newValue;
    }, [onInputChange]);

    // Memoize the components config object
    const customComponents = useMemo(() => ({
        Option: CustomOption,
        SingleValue: CustomSingleValue,
        Control: CustomControl,
        LoadingIndicator: CustomLoadingIndicator,
        ...overrideComponents,
    }), [CustomOption, CustomSingleValue, CustomControl, CustomLoadingIndicator, overrideComponents]);

    return (
        <Select
            id={uniqueId}
            className={fullWidth ? "w-100" : ""}
            isDisabled={isDisabled}
            isLoading={isLoading}
            isSearchable={isSearchable}
            isMulti={isMulti}
            closeMenuOnSelect={closeMenuOnSelect ?? true}
            hideSelectedOptions={hideSelectedOptions ?? true}
            placeholder={placeholder}
            options={options}
            onMenuOpen={() => setOpenMenu(true)}
            onMenuClose={() => setOpenMenu(false)}
            menuIsOpen={isDisabled || forceCloseMenu ? false : undefined}
            value={value}
            onChange={onChange}
            {...(usesControlledInput
                ? { inputValue, onInputChange: handleInputChange }
                : {})}
            filterOption={filterOption}
            getOptionValue={getOptionValue}
            menuPortalTarget={document.body}
            menuPlacement={placement}
            components={customComponents}
            classNames={{
                menu: () => 'custom__menu__list',
            }}
            styles={{
                container: (provided) => ({
                    ...provided,
                    minWidth,
                    maxWidth,
                }),
                control: (provided, state) => ({
                    ...provided,
                    borderRadius: "4px",
                    border: border || "0.5px solid rgba(240, 240, 240, 0.30)",
                    backgroundColor: background || "rgba(9, 9, 9, 0.6)",
                    boxShadow: 'none',
                    minHeight: controllHeight || '38px',
                    padding: padding || "0px 4px",
                    opacity: state.isDisabled ? 0.4 : 1,
                    '&:hover': {
                        border: border || "0.5px solid rgba(240, 240, 240, 0.30)",
                        boxShadow: 'none'
                    }
                }),
                menu: (provided) => ({
                    ...provided,
                    backgroundColor: "#000",
                }),
                option: (provided, state) => {
                    return {
                        ...provided,
                        fontSize: "12px !important",
                        backgroundColor: state.isSelected
                            ? "rgb(49, 53, 36)" // Highlight color for selected option
                            : "rgba(255, 255, 255, 0.15)",
                        color: state.isSelected
                            ? "#CCF54E" // Text color for selected option 
                            : "#FFFFFF", // Default text color
                        "&:hover": {
                            backgroundColor: state.isDisabled ? '' : state.isSelected ? 'rgb(49, 53, 36)' : "rgba(240, 240, 240, 0.20)",
                            color: state.isSelected ? '#CCF54E' : '#FFFFFF'
                        },
                        opacity: state.isDisabled ? 0.6 : 1,
                        cursor: state.isDisabled ? 'default' : 'pointer',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    };
                },
                indicatorSeparator: (provided) => ({
                    ...provided,
                    display: "none",
                }),
                indicatorsContainer: (provided) => ({
                    ...provided,
                    padding: "0px",
                }),
                placeholder: (provided) => ({
                    ...provided,
                    color: `${isLoading && mutedLoadingState ? "rgba(240, 240, 240, 0.42)" : "#57585a"} !important`,
                    fontSize: "12px !important",
                }),
                valueContainer: (provided) => ({
                    ...provided,
                    padding: "0px 4px",
                    flexWrap: isMulti ? "nowrap" : provided.flexWrap,
                    overflow: isMulti ? "hidden" : provided.overflow,
                }),
                multiValue: () => ({ display: "none" }),
                multiValueLabel: () => ({ display: "none" }),
                multiValueRemove: () => ({ display: "none" }),
                singleValue: (provided) => ({
                    ...provided,
                    fontSize: "12px",
                    color: color || '#fff',
                    textTransform: 'none'
                }),
                input: (provided) => ({
                    ...provided,
                    color: color || '#fff',
                }),
                menuList: (provided) => ({
                    ...provided,
                    maxHeight: menuHeight || "200px", // Set max-height here
                    overflowY: "auto", // Add scroll if items exceed max-height
                    paddingTop: '0px',
                    paddingBottom: '0px',
                    borderRadius: "4px 0px 4px 0px",
                    backgroundColor: "#000",
                }),
                noOptionsMessage: (provided) => ({
                    ...provided,
                    fontSize: '10px',
                    whiteSpace: 'nowrap',
                    width: '100%',
                }),
                menuPortal: (base) => ({
                    ...base,
                    zIndex: 9999
                }),
                dropdownIndicator: (provided, state) => ({
                    ...provided,
                    color: color || '#f0f0f0', // Dynamically set indicator color
                    transform: state.selectProps.menuIsOpen ? "rotate(0deg)" : "rotate(-90deg)",
                    transition: "transform 0.3s ease, color 0.3s ease", // Smooth transition
                    padding: '0px',
                    opacity: state.isDisabled ? 0.4 : 0.8,
                    '&:hover': {
                        color: color || '#f0f0f0',
                    }
                }),
            }}
        />
    )
};

export default memo(CustomSelect);
